var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { Vue, Component, Prop } from "vue-facing-decorator";
import { Notifier } from "@/common/utils/notifier";
import { BackgroundTaskStatus, isFinishedStatus, parseBackgroundTaskDetails } from "@/app/models/background-task";
/**
 * Every item of a background task and what happened to it. Generic: it shows any task whose details
 * follow BackgroundTaskDetails. Follows the message it is given, so it keeps updating while the task runs.
 */
let BackgroundTaskDetailsModal = class BackgroundTaskDetailsModal extends Vue {
    constructor() {
        super(...arguments);
        this.show = false;
        this.onlyNotDone = false;
    }
    open(onlyNotDone = false) {
        this.onlyNotDone = onlyNotDone && this.notDoneItems.length > 0;
        this.show = true;
    }
    get details() {
        var _a;
        return parseBackgroundTaskDetails((_a = this.message) === null || _a === void 0 ? void 0 : _a.details);
    }
    get items() {
        var _a, _b;
        return (_b = (_a = this.details) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : [];
    }
    /** Items that went wrong: these are what support needs to see. */
    get failedItems() {
        return this.items.filter(i => i.status === "Failed");
    }
    /** Failed, cancelled, or left undone by a task that has finished. */
    get notDoneItems() {
        return this.items.filter(i => this.isNotDone(i));
    }
    get visibleItems() {
        return this.onlyNotDone ? this.notDoneItems : this.items;
    }
    get isFinished() {
        var _a;
        return isFinishedStatus((_a = this.message) === null || _a === void 0 ? void 0 : _a.status);
    }
    get title() {
        var _a;
        return ((_a = this.message) === null || _a === void 0 ? void 0 : _a.processName) || "Background task";
    }
    get alertClass() {
        var _a;
        if (((_a = this.message) === null || _a === void 0 ? void 0 : _a.status) === BackgroundTaskStatus.Cancelled)
            return "alert-secondary";
        return this.isFinished ? "alert-danger" : "alert-warning";
    }
    get summary() {
        const done = this.items.filter(i => i.status === "Succeeded").length;
        const failed = this.failedItems.length;
        const cancelled = this.items.filter(i => i.status === "Cancelled").length;
        const waiting = this.items.length - done - failed - cancelled;
        const parts = [`${done} done`];
        if (failed)
            parts.push(`${failed} failed`);
        if (cancelled)
            parts.push(`${cancelled} cancelled`);
        if (waiting)
            parts.push(this.isFinished ? `${waiting} not done` : `${waiting} to do`);
        return `${this.items.length} item${this.items.length === 1 ? "" : "s"}: ${parts.join(", ")}`;
    }
    isNotDone(item) {
        return item.status === "Failed" || item.status === "Cancelled" || (this.isFinished && item.status !== "Succeeded");
    }
    statusText(item) {
        switch (item.status) {
            case "Succeeded": return "Done";
            case "Failed": return "Failed";
            case "Cancelled": return "Cancelled";
            case "Running": return this.isFinished ? "Not done" : "In progress";
            default: return this.isFinished ? "Not done" : "Waiting";
        }
    }
    statusBadge(item) {
        switch (item.status) {
            case "Succeeded": return "bg-success";
            case "Failed": return "bg-danger";
            case "Running": return this.isFinished ? "bg-secondary" : "bg-primary";
            default: return "bg-secondary";
        }
    }
    statusIcon(item) {
        switch (item.status) {
            case "Succeeded": return "fa fa-check-circle";
            case "Failed": return "fa fa-times-circle";
            case "Cancelled": return "fa fa-ban";
            case "Running": return this.isFinished ? "fa fa-minus-circle" : "fa fa-spinner fa-spin";
            default: return this.isFinished ? "fa fa-minus-circle" : "fa fa-clock-o";
        }
    }
    rowClass(item) {
        if (item.status === "Failed")
            return "table-danger";
        if (item.status === "Cancelled")
            return "table-secondary";
        return "";
    }
    copyProblems() {
        var _a, _b, _c, _d, _e, _f;
        const lines = [
            this.title,
            (_b = (_a = this.details) === null || _a === void 0 ? void 0 : _a.subtitle) !== null && _b !== void 0 ? _b : "",
            `Started: ${((_c = this.message) === null || _c === void 0 ? void 0 : _c.startedAt) ? new Date(this.message.startedAt).toLocaleString() : ""}`,
            ""
        ];
        for (const item of this.failedItems) {
            lines.push(item.description ? `${item.name} (${item.description})` : `${item.name}`);
            lines.push(`  ${(_d = item.message) !== null && _d !== void 0 ? _d : "Failed."}`);
            ((_e = item.errors) !== null && _e !== void 0 ? _e : []).forEach(e => lines.push(`  - ${e}`));
            if (item.errorReference)
                lines.push(`  Support reference: ${item.errorReference}`);
        }
        (_f = navigator.clipboard) === null || _f === void 0 ? void 0 : _f.writeText(lines.join("\n")).then(() => Notifier.onOk("The problems were copied. Paste them into your message to support.")).catch(() => Notifier.onError("Could not copy to the clipboard."));
    }
};
__decorate([
    Prop({ default: null }),
    __metadata("design:type", Object)
], BackgroundTaskDetailsModal.prototype, "message", void 0);
BackgroundTaskDetailsModal = __decorate([
    Component({
        name: "BackgroundTaskDetailsModal",
        expose: ["open"]
    })
], BackgroundTaskDetailsModal);
export default BackgroundTaskDetailsModal;
